import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "@/state/session";
import { DataModeBadge } from "@/components/shell/DataModeBadge";
import { Radar, ArrowRight, ShieldCheck, Zap } from "lucide-react";

export default function AuthPage({ mode = "signin" }) {
  const { signIn } = useSession();
  const nav = useNavigate();
  const [email, setEmail] = useState("alex@demo.ezisell.app");
  const [step, setStep] = useState(mode === "onboarding" ? "org" : "auth");
  const isSignup = mode === "signup";

  const enter = async () => {
    await signIn(email);
    if (isSignup) { setStep("org"); return; }
    nav("/app/dashboard");
  };
  const finishOnboarding = async () => { await signIn(email); nav("/app/dashboard"); };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-navy-950">
      {/* left brand rail */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 text-ink-inverse overflow-hidden">
        <div className="ez-aurora" aria-hidden />
        <div className="absolute inset-0 bg-navy-950/30" aria-hidden />
        <div className="relative flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-panel bg-emerald-600 grid place-items-center"><Radar className="h-5 w-5 text-white" /></div>
          <span className="text-xl font-semibold">Ezisell</span>
        </div>
        <div className="relative space-y-6 max-w-md">
          <h1 className="text-display leading-tight">The Walmart-first sourcing intelligence workstation.</h1>
          <p className="text-ink-inverse/70">Decide whether to buy, at what maximum cost, and in what test quantity — with provenance on every value and no fabricated live data.</p>
          <div className="space-y-3 text-compact text-ink-inverse/80">
            <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-500" /> Deterministic profit + verdict engine</div>
            <div className="flex items-center gap-2"><Zap className="h-4 w-4 text-emerald-500" /> Keepa-inspired first-party market history</div>
          </div>
        </div>
        <div className="relative"><DataModeBadge /></div>
      </div>

      {/* right form */}
      <div className="flex items-center justify-center p-6 lg:p-12 bg-surface-1">
        <div className="w-full max-w-sm">
          {step === "auth" && (
            <>
              <h2 className="text-ptitle text-ink-primary">{isSignup ? "Create your account" : "Sign in to Ezisell"}</h2>
              <p className="text-compact text-ink-secondary mt-1 mb-6">Demo gateway — no real credentials required in fixture mode.</p>
              <label className="text-lbl text-ink-secondary">Work email</label>
              <input data-testid="auth-email-input" value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full h-10 px-3 rounded-ctrl border border-edge-subtle bg-surface-0 text-body focus:outline-none focus:ring-2 focus:ring-signal-blue/40" />
              <label className="text-lbl text-ink-secondary mt-4 block">Password</label>
              <input type="password" data-testid="auth-password-input" defaultValue="demo-password"
                className="mt-1 w-full h-10 px-3 rounded-ctrl border border-edge-subtle bg-surface-0 text-body focus:outline-none focus:ring-2 focus:ring-signal-blue/40" />
              <button data-testid="auth-submit-btn" onClick={enter}
                className="mt-6 w-full h-10 rounded-ctrl bg-signal-blue text-white font-medium flex items-center justify-center gap-2 hover:bg-signal-blue/90 transition-colors">
                {isSignup ? "Continue" : "Sign in"} <ArrowRight className="h-4 w-4" />
              </button>
              <p className="text-compact text-ink-secondary mt-4 text-center">
                {isSignup ? "Already have an account? " : "New to Ezisell? "}
                <button onClick={() => nav(isSignup ? "/signin" : "/signup")} className="text-signal-blue font-medium">
                  {isSignup ? "Sign in" : "Create account"}
                </button>
              </p>
            </>
          )}
          {step === "org" && (
            <>
              <h2 className="text-ptitle text-ink-primary">Set up your organization</h2>
              <p className="text-compact text-ink-secondary mt-1 mb-6">Organizations isolate data and control team roles.</p>
              <label className="text-lbl text-ink-secondary">Organization name</label>
              <input data-testid="onboarding-org-input" defaultValue="Rivera Sourcing Co. (Demo)"
                className="mt-1 w-full h-10 px-3 rounded-ctrl border border-edge-subtle bg-surface-0 text-body focus:outline-none focus:ring-2 focus:ring-signal-blue/40" />
              <label className="text-lbl text-ink-secondary mt-4 block">Primary use</label>
              <select className="mt-1 w-full h-10 px-3 rounded-ctrl border border-edge-subtle bg-surface-0 text-body">
                <option>Online arbitrage</option><option>Retail arbitrage</option><option>Wholesale catalog analysis</option>
              </select>
              <button data-testid="onboarding-finish-btn" onClick={finishOnboarding}
                className="mt-6 w-full h-10 rounded-ctrl bg-emerald-600 text-white font-medium flex items-center justify-center gap-2 hover:bg-emerald-500 transition-colors">
                Enter workspace <ArrowRight className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
